import {
  DECK,
  SUITS,
  beloteTeam,
  cardPoints,
  chooseCard,
  getLegalCards,
  winningPlay,
} from "./engine.js";

export const PRACTICE_SEATS = Object.freeze([
  { id: 0, key: "south", team: 0 },
  { id: 1, key: "west", team: 1 },
  { id: 2, key: "north", team: 0 },
  { id: 3, key: "east", team: 1 },
]);

const TRUMP_WEIGHTS = Object.freeze({ J: 5.4, "9": 4.6, A: 3.1, "10": 2.5, K: 2, Q: 1.6, "8": 0.5, "7": 0.35 });

export function nextPracticeSeat(seat) {
  return (seat + 3) % 4;
}

function shuffled(cards, random) {
  const result = [...cards];
  for (let index = result.length - 1; index > 0; index -= 1) {
    const target = Math.floor(random() * (index + 1));
    [result[index], result[target]] = [result[target], result[index]];
  }
  return result;
}

function cardIds(cards) {
  return cards.map((card) => card.id);
}

function cardsFromIds(ids) {
  return ids.map((id) => DECK.find((card) => card.id === id)).filter(Boolean);
}

export function handStrength(hand, trump, center = null) {
  const cards = center ? [...hand, center] : [...hand];
  const trumps = cards.filter((card) => card.suit === trump);
  let strength = trumps.reduce((total, card) => total + TRUMP_WEIGHTS[card.rank], 0);
  strength += Math.max(0, trumps.length - 2) * 1.15;
  for (const suit of SUITS) {
    if (suit.id === trump) continue;
    const suited = cards.filter((card) => card.suit === suit.id);
    if (suited.some((card) => card.rank === "A")) strength += 2.25;
    if (suited.some((card) => card.rank === "A") && suited.some((card) => card.rank === "10")) strength += 0.85;
  }
  if (trumps.some((card) => card.rank === "K") && trumps.some((card) => card.rank === "Q")) strength += 1.8;
  return strength;
}

export function chooseAiBid(state, seat) {
  if (state.phase !== "bidding" || state.currentSeat !== seat) return null;
  const hand = state.hands[seat];
  if (state.bidRound === 1) {
    const strength = handStrength(hand, state.center.suit, state.center);
    const threshold = state.bids.filter((bid) => bid.round === 1).length === 3 ? 7.7 : 8.35;
    return strength >= threshold
      ? { action: "take", suit: state.center.suit, strength }
      : { action: "pass", suit: null, strength };
  }
  const options = SUITS.filter((suit) => suit.id !== state.center.suit)
    .map((suit) => ({ suit: suit.id, strength: handStrength(hand, suit.id, seat === state.dealer ? state.center : null) }))
    .sort((first, second) => second.strength - first.strength);
  const threshold = state.bids.filter((bid) => bid.round === 2).length === 3 ? 8.15 : 8.8;
  return options[0].strength >= threshold
    ? { action: "take", ...options[0] }
    : { action: "pass", suit: null, strength: options[0].strength };
}

export function createPracticeDeal({ dealer = 0, random = Math.random, dealNumber = 1 } = {}) {
  const deck = shuffled(DECK, random);
  const hands = [[], [], [], []];
  let cursor = 0;
  for (let batch = 0; batch < 2; batch += 1) {
    const count = batch === 0 ? 3 : 2;
    for (let offset = 1; offset <= 4; offset += 1) {
      const seat = (dealer - offset + 8) % 4;
      hands[seat].push(...deck.slice(cursor, cursor + count));
      cursor += count;
    }
  }
  const center = deck[cursor];
  cursor += 1;
  return {
    id: `practice-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    dealNumber,
    dealer,
    phase: "bidding",
    bidRound: 1,
    currentSeat: nextPracticeSeat(dealer),
    hands,
    initialHands: hands.map(cardIds),
    center,
    stock: deck.slice(cursor),
    bids: [],
    takerSeat: null,
    trump: null,
    takingTeam: null,
    leader: null,
    trick: [],
    trickNumber: 1,
    tricks: [],
    played: [],
    teamPoints: [0, 0],
    teamTricks: [0, 0],
    beloteTeam: null,
    result: null,
    history: [{ type: "deal", dealNumber, dealer }],
    decisions: [],
  };
}

function completeDistribution(state) {
  const hands = state.hands.map((hand) => [...hand]);
  const stock = [...state.stock];
  const centerRecipient = state.bidRound === 1 ? state.takerSeat : state.dealer;
  for (let offset = 1; offset <= 4; offset += 1) {
    const seat = (state.dealer - offset + 8) % 4;
    if (seat === centerRecipient) hands[seat].push(state.center, ...stock.splice(0, 2));
    else hands[seat].push(...stock.splice(0, 3));
  }
  return hands;
}

export function applyBid(state, seat, bid) {
  if (state.phase !== "bidding" || seat !== state.currentSeat) throw new Error("Ce n’est pas à ce joueur d’enchérir.");
  const action = bid?.action;
  const suit = bid?.suit || null;
  if (!["pass", "take"].includes(action)) throw new Error("Enchère inconnue.");
  if (action === "take") {
    if (!SUITS.some((item) => item.id === suit)) throw new Error("Couleur d’atout inconnue.");
    if (state.bidRound === 1 && suit !== state.center.suit) throw new Error("Au premier tour, seule la retourne peut être prise.");
    if (state.bidRound === 2 && suit === state.center.suit) throw new Error("Au second tour, il faut choisir une autre couleur.");
  }

  const next = { ...state, bids: [...state.bids], history: [...state.history] };
  next.bids.push({ round: state.bidRound, seat, action, suit, strength: bid.strength ?? null });
  next.history.push({ type: "bid", seat, action, suit: suit || null });

  if (action === "take") {
    next.takerSeat = seat;
    next.trump = suit;
    next.takingTeam = seat % 2;
    next.hands = completeDistribution(next);
    next.fullHands = next.hands.map(cardIds);
    next.beloteTeam = beloteTeam(next.hands, suit);
    next.phase = "play";
    next.leader = nextPracticeSeat(next.dealer);
    next.currentSeat = next.leader;
    next.stock = [];
    return next;
  }

  const passesThisRound = next.bids.filter((item) => item.round === state.bidRound).length;
  if (passesThisRound < 4) {
    next.currentSeat = nextPracticeSeat(seat);
    return next;
  }
  if (state.bidRound === 1) {
    next.bidRound = 2;
    next.currentSeat = nextPracticeSeat(state.dealer);
    return next;
  }
  next.phase = "passed-out";
  next.currentSeat = null;
  next.history.push({ type: "result", text: "Tout le monde passe · nouvelle donne" });
  return next;
}

export function legalCardIds(state, seat = state.currentSeat) {
  if (state.phase !== "play" || seat == null) return [];
  return getLegalCards(state.hands[seat], state.trick, state.trump, seat).map((card) => card.id);
}

export function chooseAiCard(state, seat, random = Math.random) {
  if (state.phase !== "play" || state.currentSeat !== seat) return null;
  return chooseCard({
    hand: state.hands[seat],
    trick: state.trick,
    trump: state.trump,
    seat,
    played: state.played,
    takingTeam: state.takingTeam,
    random,
  });
}

function finalizeResult(state) {
  const scoredPoints = [...state.teamPoints];
  if (state.beloteTeam !== null) scoredPoints[state.beloteTeam] += 20;
  const defendingTeam = 1 - state.takingTeam;
  const contractMade = scoredPoints[state.takingTeam] > scoredPoints[defendingTeam];
  return {
    rawPoints: [...state.teamPoints],
    scoredPoints,
    tricks: [...state.teamTricks],
    takingTeam: state.takingTeam,
    contractMade,
    winnerTeam: scoredPoints[0] === scoredPoints[1] ? null : (scoredPoints[0] > scoredPoints[1] ? 0 : 1),
    userOutcome: scoredPoints[0] > scoredPoints[1] ? "win" : scoredPoints[0] < scoredPoints[1] ? "loss" : "draw",
  };
}

export function applyCardPlay(state, seat, cardId) {
  if (state.phase !== "play" || seat !== state.currentSeat) throw new Error("Ce n’est pas à ce joueur de jouer.");
  const legal = legalCardIds(state, seat);
  if (!legal.includes(cardId)) throw new Error("Cette carte n’est pas jouable.");
  const hands = state.hands.map((hand) => [...hand]);
  const cardIndex = hands[seat].findIndex((card) => card.id === cardId);
  const [card] = hands[seat].splice(cardIndex, 1);
  const trick = [...state.trick, { seat, card }];
  const next = {
    ...state,
    hands,
    trick,
    played: [...state.played, { seat, card }],
    history: [...state.history, { type: "play", seat, card: card.id }],
  };

  if (trick.length < 4) {
    next.currentSeat = nextPracticeSeat(seat);
    return next;
  }

  const winner = winningPlay(trick, state.trump);
  const trickPoints = trick.reduce((total, play) => total + cardPoints(play.card, state.trump), 0)
    + (state.trickNumber === 8 ? 10 : 0);
  const winningTeam = winner.seat % 2;
  next.teamPoints = [...state.teamPoints];
  next.teamPoints[winningTeam] += trickPoints;
  next.teamTricks = [...state.teamTricks];
  next.teamTricks[winningTeam] += 1;
  next.tricks = [...state.tricks, {
    number: state.trickNumber,
    leader: state.leader,
    plays: trick.map((play) => ({ seat: play.seat, card: play.card.id })),
    winnerSeat: winner.seat,
    points: trickPoints,
  }];
  next.phase = "trick-result";
  next.currentSeat = null;
  next.pendingLeader = winner.seat;
  next.history.push({ type: "trick", seat: winner.seat, points: trickPoints });
  return next;
}

export function advancePracticeState(state) {
  if (state.phase !== "trick-result") return state;
  if (state.trickNumber === 8) {
    const next = { ...state, phase: "deal-result", trick: [], currentSeat: null };
    next.result = finalizeResult(next);
    next.history = [...state.history, { type: "result", outcome: next.result.userOutcome }];
    return next;
  }
  return {
    ...state,
    phase: "play",
    leader: state.pendingLeader,
    currentSeat: state.pendingLeader,
    trick: [],
    trickNumber: state.trickNumber + 1,
    pendingLeader: null,
  };
}

export function serializePracticeDeal(state) {
  return {
    id: state.id,
    dealNumber: state.dealNumber,
    dealer: state.dealer,
    players: PRACTICE_SEATS,
    initialHands: state.initialHands,
    fullHands: state.fullHands || state.hands.map(cardIds),
    center: state.center.id,
    bids: state.bids,
    takerSeat: state.takerSeat,
    trump: state.trump,
    tricks: state.tricks,
    result: state.result,
    decisions: state.decisions,
    source: "practice-engine",
  };
}

export function restoreHands(serializedHands) {
  return serializedHands.map(cardsFromIds);
}
